import React, { useState, useMemo } from 'react';
import { GoogleMap, MarkerF, InfoWindowF } from '@react-google-maps/api';
import { useTranslation } from 'react-i18next';

const mapContainerStyle = {
  width: '100%',
  height: 'calc(100vh - 180px)', // ✅ S'adapte parfaitement à l'écran du téléphone
  minHeight: '400px', // ✅ Garde une taille minimale pour être visible
  borderRadius: '15px'
};

// POINT 1 : URLs sécurisées en HTTPS
const MARKER_COLORS = {
  SCHOOL: "https://maps.google.com/mapfiles/ms/icons/red-dot.png",   
  BUS: "https://maps.google.com/mapfiles/ms/icons/yellow-dot.png",      
  STUDENT: "https://maps.google.com/mapfiles/ms/icons/blue-dot.png"   
};

export const AdminMap = ({ school, students, buses }: any) => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  
  const [selected, setSelected] = useState<any>(null);
  const [filterBus, setFilterBus] = useState<string>('all');
  const [map, setMap] = useState<google.maps.Map | null>(null);

  const schoolLat = Number(school?.latitude) || 33.58831;
  const schoolLng = Number(school?.longitude) || -7.61138;

  // Filtrage des élèves avec comparaison propre des chaînes
  const filteredStudents = useMemo(() => {
    if (!students) return [];
    if (filterBus === 'all') return students;
    return students.filter((s: any) => 
      String(s.assignedBusNumber ?? '').trim() === String(filterBus).trim()
    );
  }, [students, filterBus]);

  const centerOnSchool = () => {
    if (map) {
      map.panTo({ lat: schoolLat, lng: schoolLng });
      map.setZoom(15);
    }
  };

  return (
    <div style={{ position: 'relative', background: 'white', borderRadius: '20px', padding: '10px', direction: isRTL ? 'rtl' : 'ltr' }}>
      
      {/* HEADER */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 15px', flexWrap: 'wrap', gap: '10px', flexDirection: isRTL ? 'row-reverse' : 'row' }}>
        <h2 style={{ color: '#1a202c', margin: 0, fontWeight: 900, fontSize: '1.2rem' }}>
          📍 {t('map_live_tracking')} - {school?.schoolName || t('school_default')}
        </h2>
        
        <div style={{ display: 'flex', gap: '10px' }}>
          <select 
            value={filterBus} 
            onChange={(e) => setFilterBus(e.target.value)}
            style={{ padding: '8px 12px', borderRadius: '10px', border: '1px solid #cbd5e0', fontWeight: 'bold', cursor: 'pointer', outline: 'none' }}
          >
            <option value="all">🚀 {t('map_all_buses')}</option>
            {buses?.map((b: any) => (
              <option key={b.id} value={String(b.number)}>
                {t('bus_label')} {b.number} {b.routeDescription ? `(${b.routeDescription})` : ''}
              </option>
            ))}
          </select>

          <button 
            onClick={centerOnSchool}
            style={{ padding: '8px 12px', background: '#edf2f7', border: 'none', borderRadius: '10px', cursor: 'pointer', fontWeight: 'bold' }}
          >
            🏠 {t('map_recenter')}
          </button>
        </div>
      </div>

      {/* LÉGENDE */}
      <div style={{ 
        position: 'absolute', bottom: '30px', 
        [isRTL ? 'right' : 'left']: '25px', 
        zIndex: 10, 
        background: 'rgba(255,255,255,0.95)', padding: '12px', borderRadius: '12px', 
        boxShadow: '0 4px 12px rgba(0,0,0,0.15)', border: '1px solid #e2e8f0',
        textAlign: isRTL ? 'right' : 'left'
      }}>
        <div style={{ fontSize: '11px', fontWeight: 800, color: '#64748b', marginBottom: '8px', textTransform: 'uppercase' }}>{t('map_legend')}</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px', fontWeight: 'bold', flexDirection: isRTL ? 'row-reverse' : 'row' }}>
            <span style={{ width: '10px', height: '10px', background: 'red', borderRadius: '50%' }}></span> {t('map_legend_school')}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px', fontWeight: 'bold', flexDirection: isRTL ? 'row-reverse' : 'row' }}>
            <span style={{ width: '10px', height: '10px', background: '#FFD700', borderRadius: '50%' }}></span> {t('map_legend_bus')}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px', fontWeight: 'bold', flexDirection: isRTL ? 'row-reverse' : 'row' }}>
            <span style={{ width: '10px', height: '10px', background: '#0000FF', borderRadius: '50%' }}></span> {t('map_legend_students')} ({filteredStudents.length})
          </div>
        </div>
      </div>

      <GoogleMap
        mapContainerStyle={mapContainerStyle}
        center={{ lat: schoolLat, lng: schoolLng }}
        zoom={14}
        onLoad={(mapInstance) => setMap(mapInstance)}
        options={{ 
          mapId: "DEMO_MAP_ID", 
          gestureHandling: "greedy",
          mapTypeControl: false,
          streetViewControl: false
        }}
      >
        {/* ÉCOLE */}
        <MarkerF
          position={{ lat: schoolLat, lng: schoolLng }}
          icon={MARKER_COLORS.SCHOOL}
          label={{ text: t('map_label_school'), color: "#C53030", fontWeight: "900", fontSize: "12px" }}
          zIndex={100}
          onClick={() => setSelected({ ...school, type: 'school', lat: schoolLat, lng: schoolLng })}
        />

        {/* BUS */}
        {buses?.map((bus: any) => {
           const bLat = Number(bus.latitude);
           const bLng = Number(bus.longitude);
           if (!bLat || !bLng || isNaN(bLat) || isNaN(bLng) || bLat === 0 || bLng === 0) return null; 
           
           const isActive = filterBus === 'all' || String(filterBus) === String(bus.number);
           
           return (
            <MarkerF
              key={`bus-${bus.id}`}
              position={{ lat: bLat, lng: bLng }}
              icon={MARKER_COLORS.BUS}
              opacity={isActive ? 1 : 0.4}
              label={{ text: bus.number?.toString(), fontWeight: "bold", color: "black" }}
              zIndex={isActive ? 50 : 20}
              onClick={() => {
                setSelected({ ...bus, type: 'bus', lat: bLat, lng: bLng });
                setFilterBus(String(bus.number));
              }}
            />
           );
        })}

        {/* ÉLÈVES (POINT 2 : Validation stricte Lat + Lng) */}
        {filteredStudents.map((st: any) => {
          const sLat = Number(st.latitude ?? st.homeLatitude);
          const sLng = Number(st.longitude ?? st.homeLongitude);

          // Ignorer si les coordonnées sont absentes, zéro ou invalides (NaN)
          if (isNaN(sLat) || isNaN(sLng) || sLat === 0 || sLng === 0) return null;

          return (
            <MarkerF
              key={`st-${st.id}`}
              position={{ lat: sLat, lng: sLng }}
              icon={MARKER_COLORS.STUDENT}
              zIndex={5}
              onClick={() => setSelected({ ...st, type: 'student', lat: sLat, lng: sLng })}
            />
          );
        })}

        {/* INFOWINDOW */}
        {selected && (
          <InfoWindowF
            position={{ lat: Number(selected.lat), lng: Number(selected.lng) }}
            onCloseClick={() => setSelected(null)}
          >
            <div style={{ color: 'black', padding: '5px', minWidth: '160px', textAlign: isRTL ? 'right' : 'left', direction: isRTL ? 'rtl' : 'ltr' }}>
              <div style={{ fontWeight: 'bold', fontSize: '14px', borderBottom: '1px solid #eee', paddingBottom: '5px' }}>
                {selected.type === 'school' ? `🏫 ${t('map_legend_school')}` : 
                 selected.type === 'bus' ? `🚌 ${t('bus_label')} N°${selected.number}` : 
                 `🏠 ${selected.name}`}
              </div>
              <div style={{ marginTop: '8px', fontSize: '11px' }}>
                {selected.type === 'bus' && (
                  <>
                    {selected.routeDescription && (
                      <p style={{ margin: '2px 0 6px', color: '#2b6cb0', fontWeight: 'bold', fontSize: '12px' }}>
                        📍 {selected.routeDescription}
                      </p>
                    )}
                    <p style={{ margin: '2px 0' }}>👤 <b>{selected.driverName}</b></p>
                    <p style={{ margin: '2px 0' }}>📞 {selected.driverPhone}</p>
                    <p style={{ margin: '4px 0 0', color: selected.isTracking ? 'green' : 'orange', fontWeight: 'bold' }}>
                      ● {selected.isTracking ? t('status_on_road') : t('status_stopped')}
                    </p>
                  </>
                )}
                {selected.type === 'student' && (
                  <>
                    <p style={{ margin: '2px 0' }}>📚 {t('p_class')}: {selected.studentClass || 'N/A'}</p>
                    <p style={{ margin: '2px 0' }}>🚌 Bus N°: {selected.assignedBusNumber || 'Non assigné'}</p>
                    <p style={{ margin: '2px 0' }}>📍 {selected.address || t('err_no_address')}</p>
                  </>
                )}
              </div>
            </div>
          </InfoWindowF>
        )}
      </GoogleMap>
    </div>
  );
};